import { AfterViewInit, Component, ElementRef, OnInit, TrackByFunction, ViewChild, ComponentRef, ChangeDetectorRef } from '@angular/core';
import { FormlyType } from '../ga-formly.service';
import { ATTR_REAL_VALUE, DEFAULT_KEY_PROP, GaFormlyUtil, MIN_SEARCH_LENGTH, Select2Event, trackByFunction } from '../ga-formly.util';
import _ from 'underscore';
import { from, isObservable, noop } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { util } from '../../util';
import { IObject } from '@type_definitions/datatypes';
import { MultiselectNewOptionComponent } from './multiselect/multiselect-new-option.component';
import { DynamicComponentService } from '../../dynamic-component/dynamic-component.service';
import { TranslateService } from '@ngx-translate/core';
import { ExtraFieldType } from '../extra-field-type';
import { AjaxOptions, ProcessedResult } from 'select2';

@Component({
  selector: 'formly-field-multiselect',
  template: `
    <div #multiselectField>
      <ng-container *ngIf="to.localSearch; else remotesearch">
        <select
          class="form-control"
          [multiple]="true"
          [ngClass]="classNames"
          [attr.x-autocompletetype]="to.autoCompleteType"
          [attr.autocompletetype]="to.autoCompleteType"
          [formlyAttributes]="field"
          [inputFocus]="to.inputFocus"
        >
          <ng-container *ngIf="to.options | formlySelectOptions: field | async as opts">
            <ng-container *ngIf="to._flatOptions; else grouplist">
              <ng-container *ngFor="let opt of opts; trackBy: trackByFunction">
                <option [ngValue]="opt.value" [attr.disabled]="opt.disabled ? opt.disabled : null" [attr.${ATTR_REAL_VALUE}]="setRealValue(opt.value)" [selected]="isSelectedControlValue(opt.value)">{{ opt.label }}</option>
              </ng-container>
            </ng-container>

            <ng-template #grouplist>
              <ng-container *ngFor="let opt of opts; trackBy: trackByFunction">
                <option *ngIf="!opt.group; else optgroup" [ngValue]="opt.value" [attr.disabled]="opt.disabled ? opt.disabled : null" [attr.${ATTR_REAL_VALUE}]="setRealValue(opt.value)" [selected]="isSelectedControlValue(opt.value)">{{ opt.label }}</option>
                <ng-template #optgroup>
                  <optgroup [label]="opt.label">
                    <option *ngFor="let child of opt.group; trackBy: trackByFunction" [ngValue]="child.value" [attr.disabled]="opt.disabled ? opt.disabled : null" [attr.${ATTR_REAL_VALUE}]="setRealValue(child.value)" [selected]="isSelectedControlValue(child.value)">
                      {{ child.label }}
                    </option>
                  </optgroup>
                </ng-template>
              </ng-container>
            </ng-template>

          </ng-container>
        </select>
      </ng-container>

      <ng-template #remotesearch>
        <select class="form-select form-select-sm" [ngClass]="classNames" [attr.x-autocompletetype]="to.autoCompleteType" [attr.autocompletetype]="to.autoCompleteType" [attr.disabled]="to.disabled ? to.disabled : null"></select>
        <input class="d-none" [formControl]="formControl" [formlyAttributes]="field">
      </ng-template>
    </div>
  `
})
export class FormlyFieldMultiselectComponent extends ExtraFieldType implements OnInit, AfterViewInit {
  public defaultOptions = {
    templateOptions: {
      options: []
    }
  };

  public select2Options:Select2.Options;
  public jSelect: JQuery<HTMLSelectElement>;
  public trackByFunction: TrackByFunction<any>;

  private formatFn:Function;
  private newOptionComponentRef:ComponentRef<MultiselectNewOptionComponent>;
  private predicate: string;

  @ViewChild('multiselectField', { static: false }) private multiselectField:ElementRef<HTMLElement>;

  constructor(
    private translateService:TranslateService,
    private dynamicComponentService:DynamicComponentService,
    private formlyUtil: GaFormlyUtil,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit():void {
    this.trackByFunction = trackByFunction.bind(this);
    this.formlyUtil.initSelectField(this.to, this.formControl);
    this.to.placeholder = this.translateService.instant(this.to.placeholder || 'GA.FORM.SEARCH.PLACEHOLDER');
    this.classNames = this.formlyUtil.createClassNames(FormlyType.MULTI_SELECT, <string>this.key, this.to.label);
    this.formatFn = this.formlyUtil.getFormatFn(this.to.format);
    // set predicate of this field config
    const valueProp: string = _.isString(this.to.valueProp) ? this.to.valueProp : undefined;
    this.predicate = valueProp || this.to.trackBy || DEFAULT_KEY_PROP;
  }

  public ngAfterViewInit():void {
    // init select2
    this.jSelect = $(this.multiselectField.nativeElement).find('select');
    this.select2Init();

    // add initial value to options
    if (util.notNullOrUndefinedOrEmpty(this.formControl.value)) {
      this.setSelect2Values(this.formControl.value);
    }

    // register listener
    this.listen();
  }

  public isSelectedControlValue(value:any):boolean {
    if (util.isNullOrUndefinedOrEmpty(this.formControl.value)) {
      return false;
    }

    const fc:any[] = _.isArray(this.formControl.value)? this.formControl.value : [ this.formControl.value ];
    // check if field value (formControl value) same as provided value in option list
    return !!_.find(fc, (obj:any) => {
      return obj?.filter && obj.filter((v:IObject) => Object.prototype.hasOwnProperty.call(v, this.predicate)).length? obj[ this.predicate ] === value[ this.predicate ] : obj === value;
    });
  }

  public setRealValue(value:any):any {
    return util.toJson(value);
  }

  protected listen():void {
    super.listen();

    // get common select2 listener
    this.formlyUtil.getSelect2Listener(this.formControl, this.jSelect, true);

    this.jSelect.on(Select2Event.OPEN, () => {
      if (this.to.onOpen) {
        this.to.onOpen(); // call onOpen() when multiselect field is clicked and list of options is shown
      }
      this.addFunction();
    });

    this.jSelect.on(Select2Event.CLOSING, () => {
      if (this.to.addFunction) {
        $('.select2-results > .select2-button').remove();
        this.dynamicComponentService.remove(this.newOptionComponentRef);
      }
    });
  }

  private select2Init():void {
    const ajax:AjaxOptions = {
      dataType: 'json',
      delay: 300,
      // implement a custom transport mechanism that allows to use angular service functions
      transport: (params:{ data:{ term:string } }, success:Function, failure:Function) => {
        if (this.to.searchFunction) {
          const request = {
            abort: this.to.searchFunction.cancel
          };

          // check if its promise then it will be converted to observable
          if (!isObservable(this.to.searchFunction.request)) {
            this.to.searchFunction.request(params.data.term)
              .then(results => success(results))
              .catch(rejection => rejection !== 0? failure() : noop());
          } else {
            from(this.to.searchFunction.request(params.data.term))
              .pipe(
                catchError((rejection) => rejection !== 0? failure() : noop())
              )
              .subscribe(results => success(results));
          }

          return request;
        } else {
          failure('The parameter searchFunction is not defined');
        }
      },
      processResults: (results):ProcessedResult<any> => {
        return {
          results: _.map(results, (el:any) => {
            return {
              id: el[this.predicate],
              text: this.formatFn(el),
              realValue: el
            };
          })
        };
      }
    };

    this.select2Options = {
      multiple: true,
      placeholder: this.to.placeholder,
      templateSelection: (data:any) => {
        // Add the real object as a custom attribute
        $(data.element).attr(ATTR_REAL_VALUE, util.toJson(data.realValue));
        return data.text;
      }
    };

    if (!this.to.localSearch) {
      this.select2Options.tags = true;
      this.select2Options.ajax = ajax;
      this.select2Options.minimumInputLength = this.to.minSearchLength || MIN_SEARCH_LENGTH;
      this.select2Options.createTag = (params:IObject) => {
        // allow to include the searched text in the list of options when the text passes customValueTestFn validator function
        if (this.to.customValueTestFn && this.to.customValueTestFn(params.term)) {
          return {
            id: params.term,
            text: params.term,
            realValue: params.term
          };
        }
      };

      this.formControl.valueChanges.subscribe(values => setTimeout(() => {
        this.setSelect2Values(values);
      }));
    }

    this.jSelect.select2(this.select2Options);
  }

  private setSelect2Values(newValues:({} | string)[]):void {
    newValues = util.isNullOrUndefined(newValues)? undefined : (_.isArray(newValues)? newValues : [ newValues ]);
    const currentValues:({} | string)[] = this.formlyUtil.getSelect2Values(this.jSelect, true);

    _.each(newValues, (newValue:{} | string) => {
      const foundCurrentValue:any = _.find(currentValues, (currentValue:{} | string) => {
        return newValue[this.predicate] === currentValue[this.predicate] || newValue === currentValue;
      });

      if (!foundCurrentValue) { // when not found in the current list, add the option to the select
        let newOption:HTMLOptionElement;

        if (_.isObject(newValue)) {
          // object - eg. { "id": 73897, "firstname": "Marius", "lastname": "Hi" }
          newOption = new Option(this.formatFn(newValue), newValue[this.predicate], true, true);

        } else {
          // string - eg. manual_email@acslworld.com
          newOption = new Option(<string>newValue, <string>newValue, true, true);
        }

        // store the real value as an attribute
        newOption.setAttribute(ATTR_REAL_VALUE, util.toJson(newValue));

        if (this.to.localSearch) {
          this.to.options = this.to.options && this.to.options.length? this.to.options.concat(newValue) : [ newValue ];
        } else {
          // Append it to the select
          this.jSelect.append(newOption);
        }
      }
    });

    _.each(currentValues, (currentValue:{} | string) => {
      const foundNewValue:{} | string = _.find(newValues, (newValue:any) => {
        return currentValue[this.predicate] === newValue[this.predicate] || currentValue === newValue;
      });

      if (!foundNewValue) { // when not found in the new list, remove the option
        $(this.jSelect.find(`option[value="${_.isObject(currentValue)? currentValue[this.predicate] : currentValue}"]`)).remove();
      }
    });
    
    this.changeDetectorRef.detectChanges();
  }

  // add a button at the end of the search list
  private addFunction:Function = () => {
    if (this.to.addFunction) {
      // add placeholder where to add the content of the component
      $('.select2-results:not(:has(a))').append('<div class="select2-button"></div>');

      // add the component content and its bindings
      this.newOptionComponentRef = this.dynamicComponentService.add(MultiselectNewOptionComponent, '.select2-results > .select2-button');
      this.newOptionComponentRef.instance.label = this.to.addFunction.label;
      this.newOptionComponentRef.instance.onClick.subscribe(() => {
        const searchTerm:string = $(this.multiselectField.nativeElement).find('input.select2-search__field').val();
        this.to.addFunction.onClick(searchTerm);
        this.jSelect.select2('close');
      });
    }
  };
}